import { BoxOfferRulePeriod, OfferAutonomousPurchase } from "@/modules/boxOffer/entities/boxOfferTypes"
import { CoachOfferType, CoachPaymentOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { type CoachOffersApiGateway } from "@modules/coachOffers/gateway/coachOffersApi.gateway"
import {
  type CoachOfferCreationInput,
  type CoachOfferDetailsDto,
  type CoachOfferPriceDto,
  type CoachOfferUpdateInput,
  type PaymentOfferCreationInput,
} from "@modules/coachOffers/gateway/coachOffersDto"
import { type UserProgramDetailsDto } from "@modules/programs/gateway/programDto"
import { type CreateUserProgramInputDto } from "@modules/programs/gateway/programInputDto"
import { ProgramStatus, ProgramSubscriptionStatus } from "@modules/programs/types/programTypes"

const FAKE_COACH_OFFER_INDIVIDUAL: CoachOfferDetailsDto = {
  id: "coach-offer-individual-1",
  type: CoachOfferType.Individual,
  name: "Suivi individuel mensuel",
  description: "Un accompagnement personnalisé chaque mois.",
  renewable: true,
  showcaseDisplay: true,
  priceDisplay: true,
  autonomousPurchase: OfferAutonomousPurchase.PUBLIC,
  archived: false,
  waiverDetails: null,
  image: null,
  displayOrder: 1,
  paymentOffers: [
    {
      id: "payment-offer-individual-1",
      price: { value: 99, currency: "EUR" },
      recurringPeriod: "P1M",
      type: CoachPaymentOfferType.Subscription,
      title: "Mensuel",
      count: null,
      minimumCommitmentPeriod: null,
      commitmentPeriodAutoRenewal: false,
      validityPeriodMonths: null,
      renewable: null,
      creationDate: "2024-01-01T00:00:00Z",
    },
  ],
  programs: [],
  rules: [
    {
      value: 2,
      period: BoxOfferRulePeriod.DAY,
    },
    {
      value: 12,
      period: BoxOfferRulePeriod.WEEK,
    },
  ],
}

const FAKE_COACH_OFFER_COLLECTIVE: CoachOfferDetailsDto = {
  id: "coach-offer-collective-1",
  type: CoachOfferType.Collective,
  name: "Cours collectifs",
  description: "Des séances en petit groupe.",
  renewable: false,
  showcaseDisplay: true,
  priceDisplay: true,
  autonomousPurchase: OfferAutonomousPurchase.PUBLIC,
  image: null,
  archived: false,
  waiverDetails: null,
  displayOrder: 2,
  paymentOffers: [
    {
      id: "payment-offer-collective-1",
      price: { value: 49, currency: "EUR" },
      recurringPeriod: null,
      type: CoachPaymentOfferType.TicketsBook,
      title: "10 séances",
      count: 10,
      minimumCommitmentPeriod: null,
      commitmentPeriodAutoRenewal: false,
      validityPeriodMonths: 3,
      renewable: null,
      creationDate: "2024-01-01T00:00:00Z",
    },
  ],
  programs: [],
  rules: [
    {
      value: 2,
      period: BoxOfferRulePeriod.DAY,
    },
    {
      value: 12,
      period: BoxOfferRulePeriod.WEEK,
    },
  ],
}

export const COACH_OFFER_TEST_DATA = {
  FAKE_COACH_OFFER_INDIVIDUAL,
  FAKE_COACH_OFFER_COLLECTIVE,
}

export class FakeCoachOffersApi implements CoachOffersApiGateway {
  offers: CoachOfferDetailsDto[] = [FAKE_COACH_OFFER_INDIVIDUAL, FAKE_COACH_OFFER_COLLECTIVE]

  givenOffers(offers: CoachOfferDetailsDto[]) {
    this.offers = offers
  }

  getCoachOffers(): Promise<CoachOfferDetailsDto[]> {
    return Promise.resolve(this.offers)
  }

  getCoachOffer(id: string): Promise<CoachOfferDetailsDto> {
    const offer = this.offers.find((o) => o.id === id)
    if (!offer) return Promise.reject(new Error(`Offer ${id} not found`))
    return Promise.resolve(offer)
  }

  createCoachOffer(_input: CoachOfferCreationInput): Promise<CoachOfferDetailsDto> {
    return Promise.resolve(FAKE_COACH_OFFER_INDIVIDUAL)
  }

  updateCoachOffer(_id: string, _input: CoachOfferUpdateInput): Promise<CoachOfferDetailsDto> {
    return Promise.resolve(FAKE_COACH_OFFER_INDIVIDUAL)
  }

  deleteCoachOffer(_id: string): Promise<void> {
    return Promise.resolve()
  }

  createPaymentOffer(_offerId: string, _input: PaymentOfferCreationInput): Promise<CoachOfferPriceDto> {
    if (FAKE_COACH_OFFER_INDIVIDUAL.paymentOffers[0])
      return Promise.resolve(FAKE_COACH_OFFER_INDIVIDUAL.paymentOffers[0])
    return Promise.reject(new Error("No payment offer available"))
  }

  updateCoachOfferPaymentOffer(
    _offerId: string,
    _paymentOfferId: string,
    _input: PaymentOfferCreationInput,
  ): Promise<CoachOfferPriceDto> {
    if (FAKE_COACH_OFFER_INDIVIDUAL.paymentOffers[0])
      return Promise.resolve(FAKE_COACH_OFFER_INDIVIDUAL.paymentOffers[0])
    return Promise.reject(new Error("No payment offer available"))
  }

  deleteCoachOfferPaymentOffer(_offerId: string, _paymentOfferId: string): Promise<void> {
    return Promise.resolve()
  }

  arrangeOffersDisplayOrder(_orderMap: Record<string, number>): Promise<void> {
    return Promise.resolve()
  }

  createCoachProgram(_input: CreateUserProgramInputDto): Promise<UserProgramDetailsDto> {
    return Promise.resolve({
      id: "fake-coach-program-1",
      name: _input.name,
      description: _input.description,
      image: null,
      owner: null,
      visibility: _input.visibility,
      subscriptionStatus: ProgramSubscriptionStatus.INACTIVE,
      subscriptionCount: 0,
      price: null,
      entranceCheckEnabled: _input.entranceCheckEnabled,
      invitationLink: null,
      defaultHideSessionContent: _input.defaultHideSessionContent ?? false,
      status: ProgramStatus.DRAFT,
      duration: _input.duration,
      type: _input.type,
      sessionCount: 0,
      trialDuration: null,
      scoreboardVisibility: _input.scoreboardVisibility,
      programBundleSubscriptionId: null,
      sharedCoaches: null,
      subscribed: false,
      isEligibleToTrial: null,
      programBundle: null,
    })
  }
}
